import { motion, useTransform, type MotionValue } from "framer-motion";

interface BackgroundAnimationsProps {
  scrollYProgress: MotionValue<number>;
}

export default function BackgroundAnimations({
  scrollYProgress,
}: BackgroundAnimationsProps) {
  const y1 = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const y2 = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const y3 = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  const opacity1 = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0.4, 0.8, 0.6, 0.2]);
  const opacity2 = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0.3, 0.7, 0.5, 0.1]);
  const opacity3 = useTransform(scrollYProgress, [0, 0.4, 0.6, 1], [0.2, 0.6, 0.8, 0.3]);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {/* Network — Top Right */}
      <motion.div style={{ y: y1, opacity: opacity1 }} className="absolute top-20 right-20 w-80 h-80">
        <div className="relative w-full h-full">
          <motion.div
            className="absolute top-1/4 left-1/4 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 3, repeat: Infinity, delay: 0 }}
          />
          <motion.div
            className="absolute top-1/3 right-1/4 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 3, repeat: Infinity, delay: 0.5 }}
          />
          <motion.div
            className="absolute bottom-1/3 left-1/3 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 3, repeat: Infinity, delay: 1 }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/3 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 3, repeat: Infinity, delay: 1.5 }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 3, repeat: Infinity, delay: 2 }}
          />
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 320 320">
            <motion.line x1="80" y1="80" x2="240" y2="107" stroke="#4D7CFE" strokeWidth="0.5" opacity="0.4"
              initial={{ pathLength: 0 }} animate={{ pathLength: [0, 1, 0] }}
              transition={{ duration: 4, repeat: Infinity, delay: 0 }} />
            <motion.line x1="240" y1="107" x2="213" y2="213" stroke="#4D7CFE" strokeWidth="0.5" opacity="0.4"
              initial={{ pathLength: 0 }} animate={{ pathLength: [0, 1, 0] }}
              transition={{ duration: 4, repeat: Infinity, delay: 1 }} />
            <motion.line x1="213" y1="213" x2="107" y2="213" stroke="#4D7CFE" strokeWidth="0.5" opacity="0.4"
              initial={{ pathLength: 0 }} animate={{ pathLength: [0, 1, 0] }}
              transition={{ duration: 4, repeat: Infinity, delay: 2 }} />
            <motion.line x1="107" y1="213" x2="160" y2="160" stroke="#4D7CFE" strokeWidth="0.5" opacity="0.4"
              initial={{ pathLength: 0 }} animate={{ pathLength: [0, 1, 0] }}
              transition={{ duration: 4, repeat: Infinity, delay: 3 }} />
          </svg>
        </div>
      </motion.div>

      {/* Network — Left Side */}
      <motion.div style={{ y: y2, opacity: opacity2 }} className="absolute top-1/3 left-16 w-60 h-60">
        <div className="relative w-full h-full">
          <motion.div
            className="absolute top-1/5 left-1/5 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.2, 0.8, 0.2] }}
            transition={{ duration: 4, repeat: Infinity, delay: 0.5 }}
          />
          <motion.div
            className="absolute top-2/5 right-1/5 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.2, 0.8, 0.2] }}
            transition={{ duration: 4, repeat: Infinity, delay: 1.5 }}
          />
          <motion.div
            className="absolute bottom-2/5 left-2/5 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.2, 0.8, 0.2] }}
            transition={{ duration: 4, repeat: Infinity, delay: 2.5 }}
          />
          <motion.div
            className="absolute bottom-1/5 right-2/5 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.2, 0.8, 0.2] }}
            transition={{ duration: 4, repeat: Infinity, delay: 3.5 }}
          />
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 240 240">
            <motion.line x1="48" y1="48" x2="192" y2="96" stroke="#4D7CFE" strokeWidth="0.5" opacity="0.3"
              initial={{ pathLength: 0 }} animate={{ pathLength: [0, 1, 0] }}
              transition={{ duration: 5, repeat: Infinity, delay: 1 }} />
            <motion.line x1="192" y1="96" x2="96" y2="144" stroke="#4D7CFE" strokeWidth="0.5" opacity="0.3"
              initial={{ pathLength: 0 }} animate={{ pathLength: [0, 1, 0] }}
              transition={{ duration: 5, repeat: Infinity, delay: 2 }} />
            <motion.line x1="96" y1="144" x2="144" y2="192" stroke="#4D7CFE" strokeWidth="0.5" opacity="0.3"
              initial={{ pathLength: 0 }} animate={{ pathLength: [0, 1, 0] }}
              transition={{ duration: 5, repeat: Infinity, delay: 3 }} />
          </svg>
        </div>
      </motion.div>

      {/* Network — Bottom Right */}
      <motion.div style={{ y: y3, opacity: opacity3 }} className="absolute bottom-20 right-16 w-72 h-72">
        <div className="relative w-full h-full">
          <motion.div
            className="absolute top-1/6 left-1/6 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.1, 0.6, 0.1] }}
            transition={{ duration: 5, repeat: Infinity, delay: 1 }}
          />
          <motion.div
            className="absolute top-1/3 right-1/6 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.1, 0.6, 0.1] }}
            transition={{ duration: 5, repeat: Infinity, delay: 2 }}
          />
          <motion.div
            className="absolute bottom-1/3 left-1/3 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.1, 0.6, 0.1] }}
            transition={{ duration: 5, repeat: Infinity, delay: 3 }}
          />
          <motion.div
            className="absolute bottom-1/6 right-1/3 w-1 h-1 bg-primary rounded-full"
            animate={{ opacity: [0.1, 0.6, 0.1] }}
            transition={{ duration: 5, repeat: Infinity, delay: 4 }}
          />
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 280 280">
            <motion.line x1="47" y1="47" x2="233" y2="93" stroke="#4D7CFE" strokeWidth="0.5" opacity="0.2"
              initial={{ pathLength: 0 }} animate={{ pathLength: [0, 1, 0] }}
              transition={{ duration: 6, repeat: Infinity, delay: 1.5 }} />
            <motion.line x1="233" y1="93" x2="93" y2="187" stroke="#4D7CFE" strokeWidth="0.5" opacity="0.2"
              initial={{ pathLength: 0 }} animate={{ pathLength: [0, 1, 0] }}
              transition={{ duration: 6, repeat: Infinity, delay: 2.5 }} />
            <motion.line x1="93" y1="187" x2="187" y2="233" stroke="#4D7CFE" strokeWidth="0.5" opacity="0.2"
              initial={{ pathLength: 0 }} animate={{ pathLength: [0, 1, 0] }}
              transition={{ duration: 6, repeat: Infinity, delay: 3.5 }} />
          </svg>
        </div>
      </motion.div>

      {/* Floating single points */}
      <motion.div
        style={{ y: y1, opacity: opacity1 }}
        className="absolute top-1/4 left-1/3 w-1 h-1 bg-primary rounded-full"
        animate={{ opacity: [0.1, 0.5, 0.1] }}
        transition={{ duration: 6, repeat: Infinity, delay: 2 }}
      />
      <motion.div
        style={{ y: y2, opacity: opacity2 }}
        className="absolute top-2/3 left-1/4 w-1 h-1 bg-primary rounded-full"
        animate={{ opacity: [0.1, 0.4, 0.1] }}
        transition={{ duration: 7, repeat: Infinity, delay: 3 }}
      />
      <motion.div
        style={{ y: y3, opacity: opacity3 }}
        className="absolute top-1/2 right-1/3 w-1 h-1 bg-primary rounded-full"
        animate={{ opacity: [0.1, 0.6, 0.1] }}
        transition={{ duration: 8, repeat: Infinity, delay: 1 }}
      />
      <motion.div
        style={{ y: y1, opacity: opacity2 }}
        className="absolute bottom-1/3 left-2/3 w-1 h-1 bg-primary rounded-full"
        animate={{ opacity: [0.1, 0.3, 0.1] }}
        transition={{ duration: 9, repeat: Infinity, delay: 4 }}
      />
    </div>
  );
}
